import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Box, Typography, Button, IconButton, Divider, CircularProgress, Backdrop } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import axios from 'axios';
import { API_BASE_URL } from '../../../lib/constants';

import { useIntraOpNursingRecord } from './hooks/useIntraOpNursingRecord';
import BasicInfoSection from './screens/BasicInfoSection';
import TimingsSection from './screens/TimingsSection';
import PositionSection from './screens/PositionSection';
import EquipmentsSection from './screens/EquipmentsSection';
import StaffSection from './screens/StaffSection';
import PackingSection from './screens/PackingSection';
import BloodTransfusionSection from './screens/BloodTransfusionSection';
import PathologySection from './screens/PathologySection';
import NursingNotesSection from './screens/NursingNotesSection';
import SignatureSection from './screens/SignatureSection';
import SignatureDialog from '../components/SignatureDialog';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

const IntraOperativeNursingRecord = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  
  const {
    formData, setFormData, errors, loading, success,
    signatureDialogOpen, setSignatureDialogOpen, signaturePadRef,
    handleChange, handleSignatureOpen, handleSignatureSave, handleSubmit
  } = useIntraOpNursingRecord(uhid_no, navigate);

  const handleShare = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/generate_for_intra_op_share_token/${uhid_no}`);
      const link = `${API_BASE_URL}/intraOperativeNursingRecord/${uhid_no}?token=${res.data.token}`;
      await navigator.clipboard.writeText(link);
      window.open(`http://wa.me/+916379605575?text=${encodeURIComponent("Filling Intra-Op Record: " + link)}`, '_blank');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading && !formData.ip_no) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ my: 4 }}>
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
          
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LocalHospitalIcon sx={{ color: '#008080', fontSize: 40 }} />
              <Typography variant="h4" sx={{ color: '#008080', fontWeight: 'bold' }}>
                INTRA OPERATIVE NURSING RECORD
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ height: 40 }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 'bold' }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Action Bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: '#f0f4f4', borderRadius: 2 }}>
            <Button variant="outlined" onClick={() => navigate(`/mainPage/${uhid_no}`, { state: { from: 'IntraOperativePhase', step: 2 } })} color="primary">Back</Button>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="contained" onClick={() => navigate('/home')} color="primary">Home</Button>
                <Button variant="contained" color="secondary" onClick={() => navigate(`/video?form=INTRA OPERATIVE NURSING RECORD&uhid_no=${uhid_no}`)}>Add Video</Button>
                <IconButton onClick={handleShare} sx={{ color: '#008080' }}><ShareIcon /></IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Form Sections */}
          <BasicInfoSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} uhid_no={uhid_no} />
          
          <Divider sx={{ my: 4 }} />
          <TimingsSection formData={formData} handleChange={handleChange} />
          
          <Divider sx={{ my: 4 }} />
          <PositionSection formData={formData} handleChange={handleChange} />
          
          <Divider sx={{ my: 4 }} />
          <EquipmentsSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} />
          
          <Divider sx={{ my: 4 }} />
          <StaffSection formData={formData} handleChange={handleChange} errors={errors} />
          
          <Divider sx={{ my: 4 }} />
          <PackingSection formData={formData} handleChange={handleChange} handleSignatureOpen={handleSignatureOpen} />
          
          <Divider sx={{ my: 4 }} />
          <BloodTransfusionSection formData={formData} handleChange={handleChange} />
          
          <Divider sx={{ my: 4 }} />
          <PathologySection formData={formData} handleChange={handleChange} />
          
          <Divider sx={{ my: 4 }} />
          <NursingNotesSection formData={formData} handleChange={handleChange} />
          
          <Divider sx={{ my: 4 }} />
          <SignatureSection formData={formData} handleChange={handleChange} handleSignatureOpen={handleSignatureOpen} />

          {/* Form Footer */}
          <Box sx={{ mt: 6, p: 3, bgcolor: '#f0f4f4', borderRadius: 3, display: 'flex', gap: 2 }}>
            <DraftSaveButton
                formKey={DRAFT_KEYS.INTRAOPERATIVE_NURSING_RECORD}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draft) => setFormData(prev => ({ ...prev, ...draft }))}
                isUpdate={success === "success"}
                fullWidth
            />
            <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmit}
                sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' }, height: 56, fontWeight: 'bold' }}
            >
                {success === "success" ? 'Update Record' : 'Submit Record'}
            </Button>
          </Box>

        </Paper>

        <SignatureDialog
          open={signatureDialogOpen}
          onClose={() => setSignatureDialogOpen(false)}
          onClear={() => signaturePadRef.current.clear()}
          onSave={handleSignatureSave}
          signaturePadRef={signaturePadRef}
        />
      </Container>
    </ThemeProvider>
  );
};

export default IntraOperativeNursingRecord;
